import { FC, useState } from 'react'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { DeltaBadge } from 'src/charts/TopicsSubtopics/DeltaBadge'
import { SentimentBar } from 'src/charts/TopicsSubtopics/SentimentBar'
import { SentimentHoverCard } from 'src/charts/TopicsSubtopics/SentimentHoverCard'
import { TopicsEmptyState } from 'src/charts/TopicsSubtopics/TopicsEmptyState'
import { CONTENT_TOPICS_COPY } from 'src/charts/TopicsSubtopics/copy'
import { ContentAnalysisV2Range } from 'src/hooks/useContentAnalysisV2Range'
import { barFillRatio } from 'src/interfaces/topics.interface'
import { useGetContentAnalysisTopicsQuery } from 'src/reactQuery/useGetContentAnalysisTopicsQuery'
import {
  ContentAnalysisV2Scope,
  ContentTopicRow,
  ContentTopicsBy,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisV2.interface'
import styles from './contentTopics.module.scss'
import { ContentTopicDetailModal } from './ContentTopicDetailModal'
import { platformLabel } from '../shared'

interface ContentTopicsSectionProps {
  analysisIds?: string[]
  range: ContentAnalysisV2Range
  // List page: related comments in the popup name their analysis.
  showCampaign?: boolean
}

// The design shows five rows; the rest open in place.
const COLLAPSED_ROWS = 5

const Chevron: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <path
      d="M6 3.5L10.5 8L6 12.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const TopicRow: FC<{
  by: ContentTopicsBy
  max: number
  onOpen: () => void
  row: ContentTopicRow
}> = ({ by, max, onOpen, row }) => (
  <div
    className={styles.row}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.target === event.currentTarget && event.key === 'Enter') {
        onOpen()
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className={styles.topicCell}>
      <TruncatedTooltip
        className={styles.topicName}
        maxLines={4}
        text={row.name}
      />
      <SentimentHoverCard
        className={styles.topicBarWrap}
        sentiment={row.sentiment}
        total={row.count}
        totalLabel={CONTENT_TOPICS_COPY[by].totalLabel}
      >
        <SentimentBar
          fill={barFillRatio(row.count, max)}
          flat
          sentiment={row.sentiment}
        />
      </SentimentHoverCard>
    </div>
    <span className={styles.count}>{row.count.toLocaleString()}</span>
    <span className={styles.deltaCell}>
      {row.delta === null ? (
        <span className={styles.deltaNone}>—</span>
      ) : (
        <span
          className={`${styles.deltaPill} ${
            row.delta < 0 ? styles.deltaDown : ''
          }`}
        >
          <DeltaBadge value={row.delta} />
        </span>
      )}
    </span>
    <span className={styles.platformCell}>
      {row.topPlatform && (
        <span className={styles.platformPill}>
          <span className={styles.platformIcon}>
            {SOCIAL_ICONS[row.topPlatform] ?? SOCIAL_ICONS.earth}
          </span>
          <span className={styles.platformName}>
            {platformLabel(row.topPlatform)}
          </span>
        </span>
      )}
    </span>
    <span className={styles.chevron}>
      <Chevron />
    </span>
  </div>
)

const TopicsTable: FC<{
  by: ContentTopicsBy
  onOpen: (name: string) => void
  scope: ContentAnalysisV2Scope
}> = ({ by, onOpen, scope }) => {
  const copy = CONTENT_TOPICS_COPY[by]
  const [expanded, setExpanded] = useState(false)
  const { data, isError, isFetching, isLoading } =
    useGetContentAnalysisTopicsQuery(scope, by)
  const rows = [...(data?.rows ?? [])].sort((a, b) => b.count - a.count)
  const shown = expanded ? rows : rows.slice(0, COLLAPSED_ROWS)
  const max = rows.reduce((acc, row) => Math.max(acc, row.count), 0)
  const loading = isLoading && !data
  // Posts or comments in range; `total` counts topic rows.
  const inRange = data ? data.items ?? data.total : 0
  const topicCount = Math.max(data?.total ?? 0, rows.length)
  const countHeading = copy.noun.charAt(0).toUpperCase() + copy.noun.slice(1)

  const header = (
    <div className={styles.cardHeader}>
      <h2 className={styles.title}>{copy.title}</h2>
      <p className={styles.subtitle}>{copy.subtitle}</p>
    </div>
  )

  if (isError && !data) {
    return (
      <section className={styles.card}>
        {header}
        <div className={styles.note}>
          Topics could not be loaded. Please try again later.
        </div>
      </section>
    )
  }

  // Nothing in the range at all: an empty period, not a missing analysis.
  if (!loading && rows.length === 0 && inRange === 0) {
    return (
      <section className={styles.card}>
        {header}
        <div className={styles.note}>{`No ${copy.noun} in this period`}</div>
      </section>
    )
  }

  // Items in range but none tagged: the topics job has not run on them yet.
  if (!loading && rows.length === 0 && (data?.tagged ?? 0) === 0) {
    return (
      <section className={styles.card}>
        {header}
        <TopicsEmptyState title="Topics are not ready yet" />
      </section>
    )
  }

  return (
    <section className={styles.card}>
      {header}
      <div
        className={`${styles.table} ${
          isFetching && !loading ? styles.dimmed : ''
        }`}
      >
        <div className={styles.tableHead}>
          <span>Topic</span>
          <span>{countHeading}</span>
          <span>Delta</span>
          <span>Top Platform</span>
          <span />
        </div>
        {loading &&
          Array.from({ length: COLLAPSED_ROWS }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className={styles.row} key={index}>
              <span className={styles.skeletonLine} />
              <span className={styles.skeletonLine} />
              <span className={styles.skeletonLine} />
              <span className={styles.skeletonLine} />
              <span />
            </div>
          ))}
        {!loading && rows.length === 0 && (
          <div
            className={styles.note}
          >{`No ${copy.noun} matched a topic in this period`}</div>
        )}
        {!loading &&
          shown.map((row) => (
            <TopicRow
              by={by}
              key={row.name}
              max={max}
              onOpen={() => onOpen(row.name)}
              row={row}
            />
          ))}
      </div>
      {!loading && rows.length > COLLAPSED_ROWS && (
        <button
          className={styles.viewAll}
          onClick={() => setExpanded((value) => !value)}
          type="button"
        >
          {expanded ? 'Show less' : `View all ${topicCount} topics`}
        </button>
      )}
      {/* More topics than one read returns: say so rather than imply the
          list is complete. */}
      {!loading && expanded && topicCount > rows.length && (
        <p className={styles.note}>
          {`Showing the top ${rows.length} of ${topicCount} topics`}
        </p>
      )}
    </section>
  )
}

/**
 * Topics & Subtopics by Posts (caption sentiment) and by Comments, side by
 * side. A row opens the topic popup; its subtopics drill down inside it.
 */
export const ContentTopicsSection: FC<ContentTopicsSectionProps> = ({
  analysisIds,
  range,
  showCampaign = false,
}) => {
  const [open, setOpen] = useState<{
    by: ContentTopicsBy
    name: string
  } | null>(null)
  const scope = { ...range, analysisIds }

  return (
    <div className={styles.section}>
      <TopicsTable
        by="posts"
        onOpen={(name) => setOpen({ by: 'posts', name })}
        scope={scope}
      />
      <TopicsTable
        by="comments"
        onOpen={(name) => setOpen({ by: 'comments', name })}
        scope={scope}
      />
      {open && (
        <ContentTopicDetailModal
          by={open.by}
          onClose={() => setOpen(null)}
          scope={scope}
          showCampaign={showCampaign}
          topic={open.name}
        />
      )}
    </div>
  )
}
